/**
 * Professional Schedule Types for MEV-Med Frontend
 * Type definitions for Professional Schedule management
 *
 * @version 2025.1.0
 * @description Tipos TypeScript para gestão de Agendas de Profissionais
 */

/**
 * Week day enumeration (0 = Sunday, 6 = Saturday)
 */
export enum WeekDay {
  SUNDAY = 0,
  MONDAY = 1,
  TUESDAY = 2,
  WEDNESDAY = 3,
  THURSDAY = 4,
  FRIDAY = 5,
  SATURDAY = 6
}

/**
 * Week day names in Portuguese
 */
export const WEEK_DAY_NAMES: Record<WeekDay, string> = {
  [WeekDay.SUNDAY]: 'Domingo',
  [WeekDay.MONDAY]: 'Segunda-feira',
  [WeekDay.TUESDAY]: 'Terça-feira',
  [WeekDay.WEDNESDAY]: 'Quarta-feira',
  [WeekDay.THURSDAY]: 'Quinta-feira',
  [WeekDay.FRIDAY]: 'Sexta-feira',
  [WeekDay.SATURDAY]: 'Sábado'
};

/**
 * Week day short names in Portuguese
 */
export const WEEK_DAY_SHORT_NAMES: Record<WeekDay, string> = {
  [WeekDay.SUNDAY]: 'Dom',
  [WeekDay.MONDAY]: 'Seg',
  [WeekDay.TUESDAY]: 'Ter',
  [WeekDay.WEDNESDAY]: 'Qua',
  [WeekDay.THURSDAY]: 'Qui',
  [WeekDay.FRIDAY]: 'Sex',
  [WeekDay.SATURDAY]: 'Sáb'
};

/**
 * Available slot durations in minutes
 */
export const SLOT_DURATIONS = [15, 20, 25, 30, 40, 45, 60, 90, 120] as const;
export type SlotDuration = typeof SLOT_DURATIONS[number];

/**
 * Professional Schedule entity interface
 */
export interface ProfessionalSchedule {
  id: number;
  professionalId: number;
  professionalName?: string;
  clinicId: number;
  clinicName?: string;
  weekDay: WeekDay;
  startTime: string;
  endTime: string;
  slotDuration: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * Form data for creating/updating professional schedules
 */
export interface ProfessionalScheduleFormData {
  professionalId: number;
  clinicId: number;
  weekDay: WeekDay;
  startTime: string;
  endTime: string;
  slotDuration?: number;
}

/**
 * Bulk create schedule data (for wizard)
 */
export interface BulkScheduleFormData {
  professionalId: number;
  clinicId: number;
  schedules: Array<{
    weekDay: WeekDay;
    startTime: string;
    endTime: string;
  }>;
  slotDuration?: number;
}

/**
 * Schedule replication data
 */
export interface ScheduleReplicationData {
  sourceProfessionalId: number;
  targetProfessionalIds: number[];
  clinicId: number;
}

/**
 * Filters for listing professional schedules
 */
export interface ProfessionalScheduleFilters {
  professionalId?: number;
  clinicId?: number;
  weekDay?: WeekDay;
  isActive?: boolean;
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

/**
 * Response for list endpoint with pagination
 */
export interface ProfessionalScheduleListResponse {
  schedules: ProfessionalSchedule[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * Slot generation options
 */
export interface SlotGenerationOptions {
  clinicId: number;
  professionalId?: number;
  weeks?: number;
  skipHolidays?: boolean;
}

/**
 * Slot generation result
 */
export interface SlotGenerationResult {
  generated: number;
  skipped: number;
  errors: number;
  message: string;
}

/**
 * Professional for schedule selection
 */
export interface ProfessionalForSchedule {
  id: number;
  name: string;
  specialty?: string;
  isActive: boolean;
  defaultAppointmentDuration?: number;
}

/**
 * Clinic for schedule selection
 */
export interface ClinicForSchedule {
  id: number;
  tradeName: string;
  legalName: string;
  defaultSlotDuration: number;
  serviceType?: string;
  city?: string;
  state?: string;
}

/**
 * Time slot for selection grid
 */
export interface TimeSlot {
  startTime: string;
  endTime: string;
  selected: boolean;
}

/**
 * Props for UI components
 */
export interface WeekDaySelectorProps {
  selectedDays: WeekDay[];
  onChange: (days: WeekDay[]) => void;
  disabled?: boolean;
}

export interface TimeSlotGridProps {
  slots: TimeSlot[];
  onChange: (slots: TimeSlot[]) => void;
  slotDuration: number;
  disabled?: boolean;
}

export interface ProfessionalScheduleWizardProps {
  professional: ProfessionalForSchedule;
  clinic: ClinicForSchedule;
  onComplete: (data: BulkScheduleFormData) => Promise<void>;
  onCancel: () => void;
  existingSchedules?: ProfessionalSchedule[];
}

// NOTE: companyId removed from this interface as professional schedules are
// linked to professionals and clinics (providers), not to companies

export interface ScheduleReplicationModalProps {
  open: boolean;
  onClose: () => void;
  onReplicate: (data: ScheduleReplicationData) => Promise<void>;
  sourceProfessional: ProfessionalForSchedule;
  availableProfessionals: ProfessionalForSchedule[];
  clinic: ClinicForSchedule;
}

/**
 * Helper function to generate time slots for a day
 */
export function generateTimeSlots(
  startTime: string,
  endTime: string,
  slotDuration: number
): TimeSlot[] {
  const slots: TimeSlot[] = [];
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);

  let currentMinutes = startHour * 60 + startMin;
  const endMinutes = endHour * 60 + endMin;

  while (currentMinutes + slotDuration <= endMinutes) {
    const slotStartHour = Math.floor(currentMinutes / 60);
    const slotStartMin = currentMinutes % 60;
    const slotEndMinutes = currentMinutes + slotDuration;
    const slotEndHour = Math.floor(slotEndMinutes / 60);
    const slotEndMin = slotEndMinutes % 60;

    slots.push({
      startTime: `${String(slotStartHour).padStart(2, '0')}:${String(slotStartMin).padStart(2, '0')}`,
      endTime: `${String(slotEndHour).padStart(2, '0')}:${String(slotEndMin).padStart(2, '0')}`,
      selected: false
    });

    currentMinutes += slotDuration;
  }

  return slots;
}

/**
 * Helper function to get working days from schedule list
 */
export function getWorkingDays(schedules: ProfessionalSchedule[]): WeekDay[] {
  const days = new Set<WeekDay>();
  schedules.forEach(s => days.add(s.weekDay));
  return Array.from(days).sort((a, b) => a - b);
}

// Note: formatTimeRange is exported from scheduleSlot.ts

/**
 * Helper function to get slot duration label
 */
export function getSlotDurationLabel(minutes: number): string {
  if (minutes < 60) {
    return `${minutes} min`;
  }
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (mins === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${mins}min`;
}
